import React, { useState, useEffect } from "react";
import { generateIntelligenceReport } from "@/components/services/intelligenceService";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import {
  Brain,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  RefreshCw,
  CheckCircle2,
  Thermometer,
  SprayCan,
  ClipboardCheck,
} from "lucide-react";

const SEVERITY_CONFIG = {
  critical: {
    bg: "bg-red-50",
    border: "border-red-200",
    icon: "text-red-500",
    badge: "bg-red-100 text-red-700",
    label: "Crític",
    Icon: AlertTriangle,
  },
  warning: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    icon: "text-amber-500",
    badge: "bg-amber-100 text-amber-700",
    label: "Avís",
    Icon: AlertTriangle,
  },
  info: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    icon: "text-blue-500",
    badge: "bg-blue-100 text-blue-700",
    label: "Info",
    Icon: Info,
  },
};

function IssueCard({ issue }) {
  const config = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.info;
  const { Icon } = config;

  return (
    <div className={`${config.bg} border ${config.border} rounded-xl p-3.5`}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex-shrink-0 ${config.icon}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${config.badge}`}>
              {config.label}
            </span>
          </div>
          <p className="text-sm text-gray-800 font-medium leading-snug">{issue.message}</p>
          {issue.recommendation && (
            <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
              💡 {issue.recommendation}
            </p>
          )}
          {issue.link && (
            <Link
              to={createPageUrl(issue.link)}
              className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#7B2D3F] hover:opacity-75 transition-opacity"
            >
              Veure mòdul <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

const CACHE_KEY = "aeh_intelligence_cache";
const CACHE_TTL = 30 * 60 * 1000; // 30 min

function loadCache(venueId) {
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY}_${venueId}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(venueId, data) {
  try {
    sessionStorage.setItem(`${CACHE_KEY}_${venueId}`, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

export default function WeeklyIntelligence({ venueId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!venueId) return;
    const cached = loadCache(venueId);
    if (cached) {
      setReport(cached);
      return;
    }
    loadReport();
  }, [venueId]);

  const loadReport = async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await generateIntelligenceReport(venueId);
      setReport(data);
      saveCache(venueId, data);
    } catch (e) {
      setError("No s'ha pogut carregar l'anàlisi.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#7B2D3F]/8 bg-[#f9f0f2] flex items-center justify-center">
            <Brain className="w-[18px] h-[18px] text-[#7B2D3F] animate-pulse" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Analitzant dades...</h3>
            <p className="text-xs text-gray-400">Detectant patrons dels últims 7 dies</p>
          </div>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const { summary, allIssues } = report;
  const hasIssues = summary.totalIssues > 0;
  const visibleIssues = expanded ? allIssues : allIssues.slice(0, 3);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-gray-50">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#f9f0f2] flex items-center justify-center">
            <Brain className="w-[18px] h-[18px] text-[#7B2D3F]" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Anàlisi intel·ligent</h3>
            <p className="text-xs text-gray-400">Últims 7 dies</p>
          </div>
        </div>
        <button
          onClick={loadReport}
          className="p-2 rounded-xl hover:bg-gray-50 transition-colors text-gray-400 hover:text-gray-600"
          title="Actualitzar"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Stats row */}
      <div className="px-5 py-3 grid grid-cols-3 gap-3 border-b border-gray-50">
        <div className="text-center">
          <p className={`text-xl font-extrabold ${summary.criticalCount > 0 ? "text-red-500" : "text-gray-300"}`}>
            {summary.criticalCount}
          </p>
          <p className="text-[10px] text-gray-400 font-medium">Crític</p>
        </div>
        <div className="text-center">
          <p className={`text-xl font-extrabold ${summary.warningCount > 0 ? "text-amber-500" : "text-gray-300"}`}>
            {summary.warningCount}
          </p>
          <p className="text-[10px] text-gray-400 font-medium">Avís</p>
        </div>
        <div className="text-center">
          <p className={`text-xl font-extrabold ${summary.infoCount > 0 ? "text-blue-400" : "text-gray-300"}`}>
            {summary.infoCount}
          </p>
          <p className="text-[10px] text-gray-400 font-medium">Info</p>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-4">
        {!hasIssues ? (
          <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
            <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">Cap anomalia detectada</p>
              <p className="text-xs text-emerald-600 mt-0.5">Tot sembla correcte els últims 7 dies.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {visibleIssues.map((issue, i) => (
              <IssueCard key={`issue-${i}`} issue={issue} />
            ))}

            {allIssues.length > 3 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors"
              >
                {expanded ? (
                  <><ChevronUp className="w-3.5 h-3.5" /> Veure menys</>
                ) : (
                  <><ChevronDown className="w-3.5 h-3.5" /> Veure {allIssues.length - 3} més</>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}